import type { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import { type AxiosInstance } from 'axios';
import { isEqual, isFunction, isNil, isPlainObject } from 'lodash-es';

import { isMultipartFormData } from '@/util/is';
import { toFormData, toQuerystring } from '@/util/transformer';

import { type IAxiosRequestOptions, METHOD } from './axiosRequestOptions';

const processParams = (url: string, data?: Dictionary) =>
  isNil(data)
    ? url
    : url.replace(/\/:[a-z]{1,}/gi, match => {
        const param = match.replace(/\/:/, '');
        const value = data[param];

        if (isNil(value)) throw new Error(`${param} is required`);

        delete data[param];

        return `/${value}`;
      });

const createRequest = <M, R extends Dictionary, S extends Dictionary>(
  axiosInstance: AxiosInstance,
  optionsOrOptionsGetter:
    | IAxiosRequestOptions<M>
    | (() => IAxiosRequestOptions<M>),
  data: R,
  config: AxiosRequestConfig<S> = {},
) => {
  const options = isFunction(optionsOrOptionsGetter)
    ? optionsOrOptionsGetter()
    : optionsOrOptionsGetter;

  const { headers, method, defaultData, defaultQuery = {} } = options;
  let url = options.url;

  config.headers = Object.assign(headers ?? {}, config.headers);

  try {
    url = processParams(url, data);
  } catch (error) {
    return Promise.reject(error);
  }

  let mergedData = {
    ...defaultQuery,
  };

  if (method !== METHOD.GET) {
    if (isPlainObject(defaultData)) {
      mergedData = { ...mergedData, ...defaultData };
    }
  }

  if (isPlainObject(data)) {
    mergedData = { ...mergedData, ...data };
  }

  const queryData =
    method === METHOD.GET
      ? mergedData
      : Object.keys(defaultQuery).reduce((obj, key) => {
          obj[key] = mergedData[key];
          delete mergedData[key];
          return obj;
        }, {} as Dictionary);

  if (Object.keys(queryData).length) {
    url = `${url.split('?')[0]}?${toQuerystring(queryData)}`;
  }

  if (isEqual(mergedData, {})) {
    mergedData = data;
  }

  return axiosInstance({
    url,
    method: method as Method,
    data:
      isMultipartFormData(config.headers as any) && isPlainObject(mergedData)
        ? toFormData(mergedData)
        : mergedData,
    ...config,
  }) as Promise<AxiosResponse<S>>;
};

export default createRequest;
